import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown, BookOpen } from "lucide-react";
import axios from "axios";
import { IMG, ACCESS_CARDS, IMPACT_DOMAINS, SERVICES, MANIFESTO } from "../data/content";
import { MaskedLines, Reveal, Stagger, StaggerItem } from "../components/Reveal";
import Marquee from "../components/Marquee";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[680px] overflow-hidden bg-ink-900" data-testid="hero">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.hero} alt="Smart factory automatisée" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink-900" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/30 to-ink-900/70" />

      <div className="relative z-10 h-full mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="h-px w-12 bg-gold" />
          <span className="label-mono text-gold">Excellence Opérationnelle · Industrie 4.0</span>
        </motion.div>

        <MaskedLines
          lines={["Transformez votre", "performance industrielle"]}
          className="font-display font-semibold text-paper text-[13vw] sm:text-6xl lg:text-8xl xl:text-[7.5rem] leading-[0.88] tracking-tight"
          delay={0.35}
        />

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-9">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="max-w-xl text-paper/75 text-lg"
          >
            Lean, digital, formation et intelligence artificielle réunis pour construire l'industrie de demain.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="flex flex-wrap gap-3"
          >
            <Link to="/expertise" data-testid="hero-cta-solutions" className="group inline-flex items-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-paper transition-colors duration-300">
              Découvrir nos solutions <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <Link to="/outils-apps" data-testid="hero-cta-tools" className="inline-flex items-center gap-2 rounded-full border border-paper/30 text-paper px-6 py-4 label-mono hover:bg-paper hover:text-ink transition-colors duration-300">
              Explorer nos outils
            </Link>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 hidden md:flex flex-col items-center gap-2 text-paper/50"
      >
        <span className="label-mono [writing-mode:vertical-rl]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

function Intro() {
  return (
    <section className="bg-paper py-24 md:py-36" data-testid="intro">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal>
            <span className="label-mono text-gold">[ Qui sommes-nous ]</span>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <h2 className="font-display text-3xl md:text-5xl font-medium leading-[1.05] tracking-tight">
              Nova Lean Consulting accompagne les industriels vers <span className="text-gold-grad">l'excellence opérationnelle</span> grâce au Lean, à l'Industrie 4.0 et à la formation de nouvelle génération.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-ink/60 text-lg">
              Nous conjuguons expertise de terrain et outils digitaux pour révéler la valeur cachée de vos process : moins de gaspillage, plus de productivité, une qualité durable et des équipes autonomes.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function AccessCards() {
  return (
    <section className="bg-paper pb-4 md:pb-8" data-testid="access-cards">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {ACCESS_CARDS.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <Link to={c.to} data-testid={`access-${c.title}`} className="group relative block h-72 rounded-sm overflow-hidden bg-ink-900">
                <img src={c.image} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 group-hover:scale-105 transition-all duration-[900ms] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/50 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-between p-6 text-paper">
                  <ArrowUpRight size={20} className="self-end text-paper/60 group-hover:text-gold group-hover:rotate-45 transition-all duration-300" />
                  <div>
                    <h3 className="font-display text-2xl font-semibold">{c.title}</h3>
                    <p className="text-paper/70 text-sm mt-2">{c.desc}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ImpactBand() {
  return (
    <section className="bg-ink-900 text-paper grain relative py-20 md:py-28" data-testid="impact-domains">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <span className="label-mono text-gold">[ Nos domaines d'impact ]</span>
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 max-w-2xl leading-[0.95]">
            Là où nous créons de la valeur.
          </h2>
        </Reveal>
        <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mt-12 border hairline-light rounded-sm overflow-hidden">
          {IMPACT_DOMAINS.map((d, i) => (
            <StaggerItem key={d} className="bg-ink-900 p-6 md:p-8 hover:bg-ink-800 transition-colors duration-500 min-h-[130px] flex flex-col justify-between">
              <span className="font-mono-tech text-gold text-sm">{String(i + 1).padStart(2, "0")}</span>
              <span className="font-display text-xl md:text-2xl font-medium mt-4">{d}</span>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function ServicesPreview() {
  return (
    <section className="bg-paper py-24 md:py-36" data-testid="services-preview">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Reveal><span className="label-mono text-gold">[ Nos expertises ]</span></Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 leading-[0.95]">
                Quatre leviers,<br />une seule ambition.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link to="/expertise" className="group inline-flex items-center gap-2 label-mono text-ink link-underline">
              Toute notre expertise <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.08}>
              <Link to={`/expertise#${s.id}`} className="group block relative overflow-hidden rounded-sm bg-ink-900 h-[360px] md:h-[440px]" data-testid={`service-card-${s.id}`}>
                <img src={s.image} alt={s.title} className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 group-hover:scale-105 transition-all duration-[900ms] ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-9 text-paper">
                  <div className="flex justify-between items-start">
                    <span className="font-mono-tech text-gold text-sm">{s.index}</span>
                    <ArrowUpRight size={22} className="text-paper/60 group-hover:text-gold group-hover:rotate-45 transition-all duration-300" />
                  </div>
                  <div>
                    <h3 className="font-display text-3xl md:text-4xl font-semibold">{s.title}</h3>
                    <p className="text-paper/70 mt-2 max-w-md">{s.tagline}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Manifesto() {
  return (
    <section className="bg-ink-950 text-paper grain relative py-24 md:py-36" data-testid="manifesto">
      <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <span className="label-mono text-gold">[ Pourquoi NovaLean ? ]</span>
          <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 max-w-2xl leading-[0.95]">
            Du terrain au digital.
          </h2>
        </Reveal>

        <div className="mt-16 divide-y divide-white/10 border-t hairline-light">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="group grid md:grid-cols-12 gap-4 md:gap-8 py-10 items-start hover:bg-white/[0.03] transition-colors duration-500 -mx-5 px-5 md:-mx-10 md:px-10">
                <div className="md:col-span-2 font-mono-tech text-gold text-2xl">{m.n}</div>
                <h3 className="md:col-span-4 font-display text-2xl md:text-3xl font-medium group-hover:translate-x-1 transition-transform duration-500">{m.title}</h3>
                <p className="md:col-span-6 text-paper/60 text-lg leading-relaxed">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedBook() {
  const [book, setBook] = useState(null);

  useEffect(() => {
    axios.get(`${API}/books`).then(({ data }) => {
      const list = data || [];
      setBook(list.find((b) => b.buy_url) || list[0] || null);
    }).catch(() => {});
  }, []);

  if (!book) return null;

  return (
    <section className="bg-ink-900 text-paper grain relative py-24 md:py-32 overflow-hidden" data-testid="featured-book">
      <div className="absolute -left-24 top-1/3 w-96 h-96 rounded-full bg-gold/10 blur-[110px]" />
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 relative z-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-4 lg:col-start-2">
            <div className="relative max-w-[280px] mx-auto lg:mx-0" style={{ perspective: "1000px" }}>
              <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-sm bg-gold/25" />
              <div className="relative rounded-sm overflow-hidden border border-paper/15 shadow-2xl shadow-ink-950/60" style={{ transform: "rotateY(-8deg) rotateX(2deg)" }}>
                {book.has_cover ? (
                  <img src={`${API}/books/${book.id}/cover`} alt={book.title} className="w-full h-auto block" data-testid="featured-book-cover" />
                ) : (
                  <div className="aspect-[3/4] flex flex-col items-center justify-center p-6 text-center">
                    <BookOpen size={32} className="text-gold" />
                    <span className="font-display text-lg font-semibold mt-4 leading-tight">{book.title}</span>
                  </div>
                )}
              </div>
            </div>
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal><span className="label-mono text-gold">[ Le livre ]</span></Reveal>
            <Reveal delay={0.08}>
              <h2 className="font-display text-3xl md:text-5xl font-semibold leading-[1.02] tracking-tight mt-6">
                {book.title}
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="label-mono text-paper/50 mt-4">par {book.author}</p>
              {book.description && <p className="text-paper/65 mt-5 leading-relaxed max-w-xl line-clamp-4">{book.description}</p>}
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap items-center gap-4 mt-9">
                {book.buy_url && (
                  <a href={book.buy_url} target="_blank" rel="noopener noreferrer" data-testid="featured-book-buy"
                    className="group inline-flex items-center gap-3 rounded-full bg-gold text-ink px-7 py-4 label-mono hover:bg-paper transition-colors duration-300">
                    Acheter le livre{book.price ? ` — ${book.price.toFixed(2).replace(".", ",")} €` : ""}
                    <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform" />
                  </a>
                )}
                <Link to="/livres" data-testid="featured-book-more" className="inline-flex items-center gap-2 rounded-full border border-paper/30 px-7 py-4 label-mono hover:bg-paper hover:text-ink transition-colors">
                  Tous les ouvrages
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="bg-paper py-24 md:py-36" data-testid="testimonials">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal><span className="label-mono text-gold">[ Ils nous font confiance ]</span></Reveal>
        <Reveal delay={0.1}>
          <div className="mt-8 rounded-sm border hairline bg-white px-8 py-16 md:py-20 text-center">
            <p className="font-display text-2xl md:text-4xl font-medium text-ink/70 max-w-3xl mx-auto leading-snug">
              Les premiers retours clients seront bientôt disponibles.
            </p>
            <p className="text-ink/45 mt-5 max-w-xl mx-auto">
              Nous documentons actuellement nos accompagnements. Vous souhaitez être parmi nos premières références ?
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-ink text-paper px-7 py-4 label-mono hover:bg-gold hover:text-ink transition-colors mt-8">
              Démarrer un projet
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function FinalCta() {
  return (
    <section className="bg-paper pb-24 md:pb-36" data-testid="final-cta">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <Reveal>
          <div className="relative overflow-hidden rounded-md bg-ink-900 grain text-paper px-8 py-16 md:px-16 md:py-24">
            <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-gold/15 blur-[100px]" />
            <div className="relative z-10 max-w-3xl">
              <h2 className="font-display text-4xl md:text-7xl font-semibold leading-[0.92] tracking-tight">
                Passons de la donnée <span className="text-gold-grad">au résultat.</span>
              </h2>
              <p className="mt-6 text-paper/70 text-lg max-w-xl">
                Un diagnostic industriel offert pour identifier vos gisements de performance. Sans engagement.
              </p>
              <Link to="/contact" data-testid="final-cta-btn" className="group inline-flex items-center gap-3 mt-9 rounded-full bg-gold text-ink px-8 py-4 label-mono hover:bg-paper transition-colors duration-300">
                Demander mon diagnostic <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const marqueeItems = ["Lean Management", "Industrie 4.0", "Kaizen", "Smart Factory", "5S", "TPM", "SMED", "OEE"];
  return (
    <>
      <Hero />
      <Intro />
      <AccessCards />
      <div className="bg-paper py-6 md:py-10 border-y hairline">
        <Marquee items={marqueeItems} />
      </div>
      <ImpactBand />
      <ServicesPreview />
      <Manifesto />
      <FeaturedBook />
      <Testimonials />
      <FinalCta />
    </>
  );
}
